import { useNavigate } from 'react-router';
import { brand } from '@utility/config';
import useUrlPermission from '@hooks/url-permission';
import {
  OrderSearch,
  OrdersActions,
  OrdersFilters,
  useOrderTypeTabsInHeader,
} from '@features/orders';
import OrderTabMenu from './OrderTabMenu';
import type {
  FilterOption,
  OrderListFilter,
  OrderSearchOption,
} from '@features/orders';

type HeaderProps = {
  ddFilter: OrderListFilter;
  updateAllFilter: (patch: Record<string, string>) => void;
  marketplaceOptions: FilterOption[];
  regionOptions: FilterOption[];
  userOptions: FilterOption[];
  providerOptions?: FilterOption[];
  newOrderClick: () => void;
  setOpenExportPopup: (isOpen: boolean) => void;
  setOpenImportPopup?: (isOpen: boolean) => void;
  setTableSearch?: (query: string) => void;
  status?: string;
  marketplaceLoading?: boolean;
  regionLoading?: boolean;
  spLoading?: boolean;
  isPendingShipment?: boolean;
  hasDateFilters?: boolean;
  filter?: OrderListFilter;
  filterChanged?: (key: string, value: unknown) => void;
};

export function Header({
  ddFilter,
  marketplaceLoading,
  regionLoading,
  newOrderClick,
  updateAllFilter,
  marketplaceOptions,
  regionOptions,
  providerOptions,
  userOptions,
  setOpenExportPopup,
  setOpenImportPopup,
  setTableSearch = () => {},
  status,
  spLoading = false,
  isPendingShipment,
  hasDateFilters,
  filter,
  filterChanged,
}: HeaderProps) {
  const navigate = useNavigate();
  const { disabled, hidden } = useUrlPermission(`/app/orders/list/${status}`);
  useOrderTypeTabsInHeader(brand.hideSections.orders as string[]);

  const handleResultClick = (order: OrderSearchOption) => {
    navigate(`/app/orders/${encodeURIComponent(order._id)}`);
  };

  return (
    <div className="sk:mx-3 sk:mb-1 sk:flex sk:h-12 sk:shrink-0 sk:items-center sk:gap-2">
      <OrderTabMenu />

      <div className="sk:ml-auto sk:flex sk:items-center sk:gap-2">
        <OrderSearch
          onQueryChange={setTableSearch}
          onSelect={handleResultClick}
        />
        <OrdersFilters
          values={ddFilter}
          onChange={updateAllFilter}
          marketplaces={marketplaceOptions}
          regions={regionOptions}
          providers={providerOptions}
          users={userOptions}
          outOfStock={
            isPendingShipment && filterChanged
              ? {
                  isSelected: !!filter?.oos,
                  onChange: next => filterChanged('oos', next),
                }
              : undefined
          }
          hasDateFilters={hasDateFilters}
          isLoading={marketplaceLoading || regionLoading || spLoading}
        />
        <OrdersActions
          onNewOrder={newOrderClick}
          onExport={() => setOpenExportPopup(true)}
          onImport={
            setOpenImportPopup ? () => setOpenImportPopup(true) : undefined
          }
          isDisabled={disabled}
          isHidden={hidden}
        />
      </div>
    </div>
  );
}
